import { Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useMainScreenButtonStyles } from '../../../../components/forms/mainScreenButtonStyles';
import { getConfig } from '../../../../util/configUtil';
import { hasOneOfPermission, Permissions } from '../../../../store/reducers/user';
import { getAcuityPriority, getTriageButtonColorMatrix } from '../triage/triageUtils';
import { openPatientForm } from '../../../../store/reducers/patientContext';
import { setSnackbar } from '../../../../store/reducers/globals';
import MainScreenButton from '../../../../components/forms/mainScreenButton';
import clsx from 'clsx';
import moment from 'moment';
import { MainButtonBackground } from '../formsUtil';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
    time: {
        fontWeight: 'normal',
        marginLeft: 10,
        fontSize: '90%',
        display: 'inline-block',
        opacity: 0.7,
        whiteSpace: 'break-spaces',
    },
    seenDlg: {
        width: '100%',
        '& .label-set': {
            textTransform: 'none !important',
            '& .label-part1': {
                textTransform: 'uppercase',
            },
            '& .label-part2': {
                fontWeight: 'normal',
                margin: '0 10px',
                wordBreak: 'break-word',
            },
            '& .label-part3': {
                // fontSize: "90%",
                opacity: 0.7,
                whiteSpace: 'break-spaces',
            },
        },
        '& .not-set': {},
        '& .not-set-not-mandatory': {},
    },
    description: {
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: 10,
        marginTop: 0,
        borderRadius: 8,
    },
}));

const InitialAssessmentButton = ({ value }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const classes = useStyles();
    const classesButton = useMainScreenButtonStyles();

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');

    // Permission
    // For no same RBAC as Triage (IA is initial part of Triage)
    const canSeeInitialAssessment = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_TRIAGE, Permissions.AGYLE_CAN_MANAGE_TRIAGE]));

    const isFormDone = () => {
        return Boolean(value && !!value.who && !!value?.status && value?.status !== 'new');
    };

    const isTimestamp = () => {
        return Boolean(value && value.who && value.status === 'timestamp');
    };

    const getTextForPriority = (ia) => {
        let description = 'No score';
        if (ia?.discriminator) {
            const priority = getAcuityPriority(ia?.discriminator);

            switch (priority) {
                case 1:
                    description = t('Immediate');
                    break;
                case 2:
                    description = t('Very Urgent');
                    break;
                case 3:
                    description = t('Urgent');
                    break;
                case 4:
                    description = t('Standard');
                    break;
                case 5:
                    description = t('Not Urgent');
                    break;
                default:
                    break;
            }
        }
        return description;
    };

    const getButtonBackground = (textColor) => {
        if (textColor) {
            const themeColor = isTimestamp() || !value?.discriminator;
            return themeColor ? (theme.palette.mode !== 'light' ? '#eff1f3' : '#222425') : '#000000';
        }

        if (isTimestamp()) {
            return theme.palette.mode === 'light' ? '#ffffff' : '#222425';
        }
        const priority = getAcuityPriority(value?.discriminator);
        return priority === 5 ? '#ffffff' : getTriageButtonColorMatrix(value?.discriminator);
    };

    const openAssessmentForm = () => {
        setTimeout(() => {
            dispatch(openPatientForm('initialAssessment'));
        }, 1);
    };

    const handleOpenForm = () => {
        if (canSeeInitialAssessment) {
            openAssessmentForm();
        } else
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: t('You do not have permission to access Initial Assessment.'),
                }),
            );
    };

    const timestampButton = () => {
        return (
            <MainScreenButton
                label={t('Initial Assessment')}
                colorType="notMandatory"
                filled={!!value}
                filledTextContainerBackground={getButtonBackground()}
                filledTextContainerFontColor={getButtonBackground(true)}
                value={value}
                onOpen={handleOpenForm}
                disabled={!canSeeInitialAssessment}
                isAssessment={false}
                spacialRender={() => {
                    return (
                        <div className={clsx(classesButton.formFilledDetails, classes.description)}>
                            <Typography variant="subtitle1" fontWeight="normal">
                                <div className={classes.seenDlg}>
                                    <div className="label-set" style={{ display: 'flex', flexDirection: 'column', paddingBottom: 0 }}>
                                        <div style={{ marginBottom: 0, width: '100%' }}>
                                            <span>
                                                Time Stamp <br /> {value?.who?.__description}
                                            </span>
                                            <span style={{ marginLeft: 5 }} className="label-part3">
                                                {moment(value?.when).format(defaultDateTimeFormat)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Typography>
                        </div>
                    );
                }}
            />
        );
    };

    const assessmentButton = () => {
        return (
            <MainScreenButton
                label={t('Initial Assessment')}
                customColor
                btnBackground={() => (isFormDone() ? '' : MainButtonBackground.BLUE)}
                filledTextContainerBackground={getButtonBackground()}
                filledTextContainerFontColor={getButtonBackground(true)}
                filled={isFormDone()}
                value={value}
                onOpen={handleOpenForm}
                disabled={!canSeeInitialAssessment}
                spacialRender={() => {
                    return (
                        isFormDone() && (
                            <div className={clsx(classesButton.formFilledDetails, classes.description)}>
                                <Typography variant="subtitle1" fontWeight="normal">
                                    {isFormDone() && getTextForPriority(value)}
                                </Typography>

                                <div style={{ width: '100%' }}>
                                    <Typography variant="subtitle1" fontWeight="normal">
                                        {value && value?.who?.__description}
                                        <div className={classes.time}>{value && moment(value.when).format(defaultDateTimeFormat)}</div>
                                    </Typography>
                                </div>
                            </div>
                        )
                    );
                }}
            />
        );
    };

    return isTimestamp() ? timestampButton() : assessmentButton();
};

export default InitialAssessmentButton;
